import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, setToken, clearToken, hasToken } from "./api";

interface Commander {
  id: string;
  email: string;
  name: string;
  streak: number;
  xp: number;
}

interface AuthContextType {
  commander: Commander | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [commander, setCommander] = useState<Commander | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check existing token on mount
  useEffect(() => {
    if (!hasToken()) {
      setIsLoading(false);
      return;
    }

    api
      .get<{
        id: string;
        email: string;
        name: string;
        loginStreak: number;
        xpTotal: number;
      }>("/api/auth/me")
      .then((data) => {
        setCommander({
          id: data.id,
          email: data.email,
          name: data.name,
          streak: data.loginStreak,
          xp: data.xpTotal,
        });
      })
      .catch(() => {
        clearToken();
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.post<{
      token: string;
      commander: Commander;
    }>("/api/auth/login", { email, password });

    setToken(res.token);
    setCommander(res.commander);
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setCommander(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        commander,
        isAuthenticated: !!commander,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

// Route guard component
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return null; // Or a loading screen
  }

  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
}
